import { useEffect, useState, useRef } from "react";
import { PRODUCTS } from "../../data/productsData";
import bestSellImg from "../../assets/images/bestsell.png";
import productBanner from "../../assets/images/product-banner-2.png";
import tonerImg from "../../assets/images/toner1.png";
import img2a from "../../assets/images/2a.png";
import p2Img from "../../assets/images/p2.jpeg";
import p3Img from "../../assets/images/p3.jpeg";
import productVideo from "../../assets/videos/product-video.mp4";
import { useCart } from "../../context/CartContext";
import { useWishlist } from "../../context/WishlistContext";
import "./Products.css";

function toNumericPrice(price) {
  if (typeof price === "number") return price;
  if (!price) return 0;

  const match = String(price).match(/[\d,]+(\.\d+)?/);
  if (!match) return 0;

  return Number(match[0].replace(/,/g, "")) || 0;
}

function formatPrice(product) {
  const numericPrice = toNumericPrice(product.price);
  const currency = product.currency || "Rs.";
  return `${currency} ${numericPrice.toLocaleString()}`;
}

function getDiscountedPrice(product) {
  const originalPrice = toNumericPrice(product.price);
  const discountedPrice = Math.round(originalPrice * 0.5);
  const currency = product.currency || "Rs.";
  return {
    original: `${currency} ${originalPrice.toLocaleString()}`,
    discounted: `${currency} ${discountedPrice.toLocaleString()}`,
    discountedNumeric: discountedPrice,
  };
}

function Products({ onNavigate }) {
  const [activeId, setActiveId] = useState(PRODUCTS[0]?.id);
  const [showDetail, setShowDetail] = useState(false);

  const { addItem } = useCart();
  const { toggleItem, isWishlisted } = useWishlist();

  // HERO SLIDER
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isChanging, setIsChanging] = useState(false);

  const slides = [
    {
      id: 0,
      title: "4 in 1",
      description:
        "A multifunctional serum formulated to brighten, nourish, hydrate, and support healthier-looking skin.",
    },
    {
      id: 1,
      title: "4 in 1",
      description:
        "A multifunctional serum formulated to brighten, nourish, hydrate, and support healthier-looking skin.",
    },
    {
      id: 2,
      title: "4 in 1",
      description:
        "A multifunctional serum formulated to brighten, nourish, hydrate, and support healthier-looking skin.",
    },
  ];

  useEffect(() => {
    const sliderTimer = setInterval(() => {
      setIsChanging(true);
      setTimeout(() => {
        setCurrentSlide((previous) => (previous + 1) % slides.length);
        setIsChanging(false);
      }, 250);
    }, 5000);

    return () => clearInterval(sliderTimer);
  }, [slides.length]);

  const handleSlideChange = (index) => {
    if (index === currentSlide) return;
    setIsChanging(true);
    setTimeout(() => {
      setCurrentSlide(index);
      setIsChanging(false);
    }, 250);
  };

  const activeProduct = PRODUCTS.find((product) => product.id === activeId);

  const handleCategoryClick = (product) => {
    setActiveId(product.id);
    setShowDetail(false);
  };

  const handleAddToCart = (product, quantity = 1) => {
    if (!product) return;
    const cartItem = {
      ...product,
      price: getDiscountedPrice(product).discountedNumeric,
    };
    for (let i = 0; i < quantity; i++) {
      addItem(cartItem);
    }
    onNavigate && onNavigate("/cart");
  };

  return (
    <main className="products-page">
      {/* HERO SLIDER */}
      <section className="products-hero">
        <div
          className={`products-hero-background product-banner-parallax ${
            isChanging ? "slide-changing" : ""
          }`}
          style={{
            backgroundImage: `url(${productBanner})`,
          }}
        />

        <div className="products-hero-overlay"></div>

        <div
          className={`products-hero-content ${
            isChanging ? "content-changing" : ""
          }`}
          key={currentSlide}
        >
          <h1>{slides[currentSlide].title}</h1>
          <p>{slides[currentSlide].description}</p>
        </div>

        <div className="products-slider-dots">
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              className={`slider-dot ${
                currentSlide === index ? "active" : ""
              }`}
              onClick={() => handleSlideChange(index)}
              aria-label={`Go to slide ${index + 1}`}
            >
              <span>{String(index + 1).padStart(2, "0")}</span>
            </button>
          ))}
        </div>
      </section>

      {/* HIGHLIGHT BAR */}
      <div className="products-highlight-bar">
        <p>
          Our products are :{" "}
          <strong>
            Clinically tested, Ultra Hydrating, All skin types, Made in
            Pakistan
          </strong>
        </p>
      </div>

      {/* PRODUCTS MAIN CATALOG */}
      {!showDetail ? (
        <section className="products-section">
          {/* LEFT SIDE INTRO & LIST */}
          <div className="products-intro">
            <div className="products-brand">
              YOUR
              <br />
              CARE
            </div>
            <p className="products-description">
              We create effective skincare solutions designed to care for your
              skin, with thoughtfully selected ingredients and formulas made for
              everyday use.
            </p>

            <div className="products-category-list">
              {PRODUCTS.map((product, index) => (
                <button
                  key={product.id}
                  type="button"
                  className={`products-category ${
                    activeId === product.id ? "active" : ""
                  }`}
                  onClick={() => handleCategoryClick(product)}
                >
                  <span className="category-number">
                    {String(index + 1).padStart(2, "0")}.
                  </span>
                  <span className="category-name">
                    {product.shortName || product.name}
                  </span>
                  {product.status === "coming-soon" && (
                    <span className="coming-soon-badge">SOON</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* RIGHT SIDE SHOWCASE */}
          <div className="products-showcase">
            {activeProduct?.status === "available" ? (
              <ProductCard
                product={activeProduct}
                onViewDetails={() => setShowDetail(true)}
                onWishlist={() => toggleItem(activeProduct.id)}
                isWishlisted={isWishlisted(activeProduct.id)}
                onAddToCart={() => handleAddToCart(activeProduct)}
              />
            ) : (
              <ComingSoon product={activeProduct} />
            )}
          </div>
        </section>
      ) : (
        <ProductDetail
          product={activeProduct}
          onBack={() => setShowDetail(false)}
          onWishlist={() => toggleItem(activeProduct.id)}
          isWishlisted={isWishlisted(activeProduct.id)}
          onAddToCart={handleAddToCart}
        />
      )}
    </main>
  );
}

/* ============================================================
   PRODUCT CARD (Main Catalog View)
============================================================ */
function ProductCard({
  product,
  onViewDetails,
  onWishlist,
  isWishlisted,
  onAddToCart,
}) {
  const videoRef = useRef(null);

  const handleMouseEnter = () => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  };

  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <article className="product-card">
      <div
        className="product-image-box"
        onClick={onViewDetails}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <span className="product-badge">{product.badge || "BEST-SELLER"}</span>

        {/* Catalog Main Image */}
        <img
          src={tonerImg}
          alt={product.name}
          className="product-image"
        />

        {/* HOVER VIDEO */}
        <video
          ref={videoRef}
          src={productVideo}
          className="product-hover-video"
          loop
          muted
          playsInline
        />
      </div>

      <div className="product-card-info">
        <h2 className="product-title">{product.name}</h2>
        {product.price && (
          <p className="product-price">
            <span className="original-price">{formatPrice(product)}</span>
            <span className="discounted-price">
              {getDiscountedPrice(product).discounted}
            </span>
            <span className="discount-tag">(50% OFF)</span>
          </p>
        )}

        <div className="product-actions">
          <button
            type="button"
            className="view-details-button"
            onClick={onViewDetails}
          >
            View Details <span>→</span>
          </button>

          <div className="product-icons">
            <button
              type="button"
              className={`icon-button ${isWishlisted ? "wishlisted" : ""}`}
              onClick={onWishlist}
              aria-label="Wishlist"
            >
              {isWishlisted ? "♥" : "♡"}
            </button>
            <button
              type="button"
              className="icon-button"
              aria-label="Add to cart"
              onClick={onAddToCart}
            >
              🛒
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

/* ============================================================
   COMING SOON COMPONENT
============================================================ */
function ComingSoon({ product }) {
  return (
    <div className="coming-soon-container">
      <div className="coming-soon-box">
        <span className="coming-soon-text">COMING SOON</span>
      </div>
      <h2 className="coming-soon-title">{product?.name}</h2>
      <p className="coming-soon-description">
        This product will be available soon. Stay tuned!
      </p>
    </div>
  );
}

/* ============================================================
   PEOPLE ALSO BOUGHT (RECOMMENDED)
============================================================ */
function PeopleAlsoBought() {
  // Same 50% off treatment as the main product, applied to the
  // recommended items shown here (Rs. 2,500 -> Rs. 1,250).
  const recommendedPrice = {
    original: "Rs. 2,500",
    discounted: "Rs. 1,250",
  };

  return (
    <section className="people-also-bought">
      <p className="pab-eyebrow">RECOMMENDED</p>
      <h2 className="pab-heading">people also bought</h2>
      <div className="pab-grid">
        <div className="pab-item">
          <div className="pab-image-wrap">
            <img src={p2Img} alt="Recommended product 1" />
          </div>
          <p className="pab-price">
            <span className="original-price">{recommendedPrice.original}</span>
            <span className="discounted-price">
              {recommendedPrice.discounted}
            </span>
            <span className="discount-tag">(50% OFF)</span>
          </p>
        </div>

        <div className="pab-item">
          <div className="pab-image-wrap">
            <img src={p3Img} alt="Recommended product 2" />
          </div>
          <p className="pab-price">
            <span className="original-price">{recommendedPrice.original}</span>
            <span className="discounted-price">
              {recommendedPrice.discounted}
            </span>
            <span className="discount-tag">(50% OFF)</span>
          </p>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   PRODUCT DETAIL COMPONENT
============================================================ */
function ProductDetail({
  product,
  onBack,
  onWishlist,
  isWishlisted,
  onAddToCart,
}) {
  const [quantity, setQuantity] = useState(1);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  // LIGHTBOX PREVIEW MODAL
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const strengths = product?.strengths || [
    { label: "30 mL", available: true },
    { label: "50 mL", available: false },
  ];
  const [selectedStrength, setSelectedStrength] = useState("30 mL");

  if (!product) return null;

  // Gallery Array: Slot 1 = bestsell.png, Slot 2 = 2a.png
  const galleryImages = [bestSellImg, img2a];

  const handleOpenLightbox = (index) => {
    setLightboxIndex(index);
    setIsLightboxOpen(true);
  };

  const handlePrevImage = (e) => {
    e.stopPropagation();
    setLightboxIndex((prev) =>
      prev === 0 ? galleryImages.length - 1 : prev - 1
    );
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setLightboxIndex((prev) => (prev + 1) % galleryImages.length);
  };

  return (
    <section className="product-detail">
      <button type="button" className="back-button" onClick={onBack}>
        ← Back to Products
      </button>

      <div className="product-detail-grid">
        <div className="detail-media-layout">
          {/* THUMBNAILS COLUMN */}
          <div className="detail-thumbnails-column">
            {/* THUMBNAIL 1 + NEW IN BADGE */}
            <div className="thumb-row-top">
              <button
                type="button"
                className={`thumb-button ${
                  activeImageIndex === 0 ? "active" : ""
                }`}
                onClick={() => setActiveImageIndex(0)}
              >
                <img src={galleryImages[0]} alt="Thumbnail 1" />
              </button>

              {/* NEW IN CIRCULAR BADGE */}
              <div className="circular-new-badge">NEW IN</div>
            </div>

            {/* THUMBNAIL 2 */}
            <button
              type="button"
              className={`thumb-button ${
                activeImageIndex === 1 ? "active" : ""
              }`}
              onClick={() => setActiveImageIndex(1)}
            >
              <img src={galleryImages[1]} alt="Thumbnail 2" />
            </button>
          </div>

          {/* MAIN DISPLAY IMAGE */}
          <div
            className="main-display-box"
            onClick={() => handleOpenLightbox(activeImageIndex)}
          >
            <img
              src={galleryImages[activeImageIndex]}
              alt={product.name}
              className="detail-image"
            />
          </div>
        </div>

        {/* RIGHT PRODUCT INFO */}
        <div className="detail-information">
          <p className="detail-category">SKINCARE / SERUMS</p>
          <h1 className="detail-title">{product.name}</h1>

          {product.price && (
            <p className="detail-price">
              <span className="original-price">{formatPrice(product)}</span>
              <span className="discounted-price">
                {getDiscountedPrice(product).discounted}
              </span>
              <span className="discount-tag">(50% OFF)</span>
            </p>
          )}

          <div className="strength-section">
            <span className="strength-label">STRENGTH:</span>
            <div className="strength-options">
              {strengths.map((item, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={`strength-btn ${
                    selectedStrength === item.label ? "active" : ""
                  } ${!item.available ? "disabled" : ""}`}
                  disabled={!item.available}
                  onClick={() =>
                    item.available && setSelectedStrength(item.label)
                  }
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {product.benefits?.length > 0 && (
            <div className="detail-block">
              <h3>KEY BENEFITS</h3>
              <ul className="benefits-list">
                {product.benefits.map((benefit, index) => (
                  <li key={index}>
                    <span>✓</span>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {product.ingredients && (
            <div className="detail-block">
              <h3>Active Ingredients</h3>
              <p>{product.ingredients}</p>
            </div>
          )}

          {product.usage && (
            <div className="detail-block">
              <h3>How To Use</h3>
              <p>{product.usage}</p>
            </div>
          )}

          {product.note && (
            <div className="detail-block">
              <h3>Formulation Note</h3>
              <div className="detail-note">{product.note}</div>
            </div>
          )}

          <div className="detail-actions">
            <div className="quantity-selector">
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              >
                −
              </button>
              <span>{quantity}</span>
              <button type="button" onClick={() => setQuantity((q) => q + 1)}>
                +
              </button>
            </div>

            <button
              type="button"
              className="add-cart-button"
              onClick={() => onAddToCart(product, quantity)}
            >
              Add to Cart
            </button>

            <button
              type="button"
              className="detail-wishlist-button"
              onClick={onWishlist}
            >
              {isWishlisted ? "♥ Wishlisted" : "♡ Add to Wishlist"}
            </button>
          </div>
        </div>
      </div>

      {/* PEOPLE ALSO BOUGHT — full width, below everything */}
      <PeopleAlsoBought />

      {/* LIGHTBOX MODAL */}
      {isLightboxOpen && (
        <div
          className="lightbox-modal"
          onClick={() => setIsLightboxOpen(false)}
        >
          <div
            className="lightbox-container"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={galleryImages[lightboxIndex]}
              alt="Fullscreen view"
              className="lightbox-image"
            />
            <div className="lightbox-controls">
              <button
                type="button"
                className="lightbox-nav"
                onClick={handlePrevImage}
                aria-label="Previous image"
              >
                &lt;
              </button>
              <button
                type="button"
                className="lightbox-close"
                onClick={() => setIsLightboxOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
              <button
                type="button"
                className="lightbox-nav"
                onClick={handleNextImage}
                aria-label="Next image"
              >
                &gt;
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default Products;